import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { ApiError, toApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { useI18n } from "../../../i18n";
import { useTeacherApi } from "../../teacher-settings";
import { SegGroup } from "../../../components/ui/Seg";
import { SharePolicyError, useShareTeacherApi } from "../api/shareApi";
import type { OtherNames, ShareCtx, ShareLink, ShareTarget } from "../types";
import { shareDate } from "../lib/logic";
import "../../../styles/teacher.css";
import "../../../styles/share.css";

/** 런타임 설정(features.observerShare)과 반 명부를 한 번 읽는다 — OFF 면 명부도 읽지 않는다 */
export function useShareCtx(): ShareCtx | null {
  const api = useShareTeacherApi();
  const teacherApi = useTeacherApi();
  const [ctx, setCtx] = useState<ShareCtx | null>(null);
  useEffect(() => {
    if (!api) return;
    let alive = true;
    (async () => {
      const settings = await api.settings();
      if (!alive || !settings.enabled) return;
      const students = teacherApi
        ? await teacherApi.students().catch(() => [] as { pid: string; nickname: string }[])
        : [];
      if (alive)
        setCtx({
          api,
          settings,
          students: students.map((s) => ({ pid: s.pid, nickname: s.nickname })),
        });
    })().catch(() => {});
    return () => {
      alive = false;
    };
  }, [api, teacherApi]);
  return ctx;
}

/** 만든 링크 주소(토큰은 만들 때 한 번만 온다 — 이 탭이 살아 있는 동안만 복사할 수 있다) */
const madeUrls = new Map<string, string>();

/** 기간 고르기 — 7 · 14 · 30 중 한도 안 + 한도 자체(기본 = 한도, 와이어 '30일') */
export function dayOptions(maxDays: number): number[] {
  return [...new Set([7, 14, 30, maxDays].filter((d) => d <= maxDays))].sort((a, b) => a - b);
}

/** 목록 순서 — 유효 먼저, 그다음 최근 만든 것 */
const order = (a: ShareLink, b: ShareLink) =>
  (a.state === "active" ? 0 : 1) - (b.state === "active" ? 0 : 1) ||
  Date.parse(b.createdAt ?? "") - Date.parse(a.createdAt ?? "") ||
  0;

/**
 * FS-14-02 보호자 공유 링크(SCR v0.7 검토 4-7 · v07-20 · 플래그 ON 때만) — 영상(작업) 또는 리플레이 하나에 붙는다.
 * 자녀별 행(자녀 별명 · 대상 · 만든 날 · 만료일 · 열람 수 · 상태 유효/만료/취소) · 🔗 만들기(자녀 · 기간 · 대상) · ⧉ 복사 · 🛑 취소.
 * 반 전체 일괄 만들기는 없다(링크 = 자녀 1명 · §11 #27). friend 정책이면 영상 링크 대신 리플레이 링크(422 policy).
 */
export function ShareLinks({
  ctx,
  replayId,
  jobId,
  onError,
  onDone,
}: {
  ctx: ShareCtx;
  /** 관찰자가 볼 수 있는 리플레이(릴레이 완성) — 없으면 리플레이 링크 없음 */
  replayId: string | null;
  /** 완료된 영상 작업 — 없으면 영상 링크 없음 */
  jobId: string | null;
  onError: (e: unknown) => void;
  onDone: (text: string) => void;
}) {
  const { t } = useI18n();
  const hid = useId();
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [hidden, setHidden] = useState(false);
  const [maxDays, setMaxDays] = useState(ctx.settings.maxDays);
  const [policy, setPolicy] = useState<OtherNames>(ctx.settings.otherNames);
  const [child, setChild] = useState("");
  const [days, setDays] = useState(ctx.settings.maxDays);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [made, setMade] = useState<{ linkId: string; url: string } | null>(null);
  const [, bump] = useState(0);

  const videoOk = !!jobId && policy === "all";
  const replayOk = !!replayId;
  const targets: ShareTarget[] = [
    ...(videoOk ? ["video" as const] : []),
    ...(replayOk ? ["replay" as const] : []),
  ];
  const [target, setTarget] = useState<ShareTarget | null>(null);
  const chosen: ShareTarget | null =
    target && targets.includes(target) ? target : (targets[0] ?? null);

  const load = useCallback(async () => {
    try {
      const parts = await Promise.all([
        jobId ? ctx.api.list({ jobId }) : null,
        replayId ? ctx.api.list({ replayId }) : null,
      ]);
      const got = parts.filter((p): p is NonNullable<typeof p> => p !== null);
      // 서버가 404(플래그 OFF)면 영역을 숨긴다
      if ((jobId || replayId) && got.length === 0) {
        setHidden(true);
        return;
      }
      const seen = new Map<string, ShareLink>();
      for (const p of got) for (const l of p.items) seen.set(l.linkId, l);
      const head = got[0];
      if (head?.maxDays) setMaxDays(head.maxDays);
      if (head?.otherNames) setPolicy(head.otherNames);
      setLinks([...seen.values()].sort(order));
    } catch (e) {
      setLinks((xs) => xs ?? []);
      onError(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.api, jobId, replayId]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => setDays((d) => (dayOptions(maxDays).includes(d) ? d : maxDays)), [maxDays]);

  const students = useMemo(
    () => [...ctx.students].sort((a, b) => a.nickname.localeCompare(b.nickname, "ko")),
    [ctx.students],
  );

  if (hidden) return null;
  // friend 정책 + 협동 운반 영상(볼 리플레이 경로 없음) — 만들 수 있는 링크가 없다는 안내만
  const nothing = targets.length === 0;

  const create = async () => {
    if (!chosen || !child) return;
    setBusy("create");
    try {
      const r = await ctx.api.create({
        target: chosen,
        ...(chosen === "video" ? { jobId: jobId! } : { replayId: replayId! }),
        childPid: child,
        days,
      });
      madeUrls.set(r.link.linkId, r.url);
      setMade({ linkId: r.link.linkId, url: r.url });
      setLinks((xs) =>
        [r.link, ...(xs ?? []).filter((x) => x.linkId !== r.link.linkId)].sort(order),
      );
      onDone(t("share.teacher.created"));
    } catch (e) {
      const b = (e instanceof ApiError ? e.body : null) as { field?: unknown } | null;
      if (e instanceof SharePolicyError) {
        setPolicy("friend");
        onDone(t("share.teacher.policyBlocked"));
      } else if (e instanceof ApiError && e.status === 422 && b?.field === "childPid")
        onDone(t("share.teacher.notParticipant"));
      else if (e instanceof ApiError && e.status === 422 && b?.field === "jobId")
        onDone(t("share.teacher.notReady"));
      else if (e instanceof ApiError && e.status === 422 && b?.field === "days")
        onDone(t("share.teacher.badDays", { days: maxDays }));
      else onError(e);
    } finally {
      setBusy(null);
    }
  };
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      onDone(t("share.teacher.copied"));
    } catch (e) {
      onError(toApiError(e));
    }
  };
  const revoke = async (l: ShareLink) => {
    setBusy(`revoke:${l.linkId}`);
    try {
      const r = await ctx.api.revoke(l.linkId);
      madeUrls.delete(l.linkId);
      if (made?.linkId === l.linkId) setMade(null);
      setLinks((xs) =>
        (xs ?? [])
          .map((x) => (x.linkId === l.linkId ? (r ?? { ...x, state: "revoked" as const }) : x))
          .sort(order),
      );
      onDone(t("share.teacher.revoked"));
    } catch (e) {
      onError(e);
    } finally {
      setBusy(null);
      setConfirm(null);
      bump((n) => n + 1);
    }
  };

  return (
    <section className="pf-share-links" aria-labelledby={hid} data-testid="share-links">
      <h3 id={hid} className="pf-share-links__title">
        <span aria-hidden="true">🔗 </span>
        {t("share.teacher.title")}
      </h3>
      <p className="pf-muted">{t("share.teacher.hint", { days: maxDays })}</p>
      {policy === "friend" && (
        <p className="pf-video__note" data-testid="share-policy">
          {t(nothing ? "share.teacher.policyNone" : "share.teacher.policyFriend")}
        </p>
      )}
      {!links ? null : links.length === 0 ? (
        <p className="pf-muted" data-testid="share-empty">
          {t("share.teacher.empty")}
        </p>
      ) : (
        <ul className="pf-share-links__list" data-testid="share-rows">
          {links.map((l) => {
            const url = madeUrls.get(l.linkId);
            const active = l.state === "active";
            return (
              <li
                key={l.linkId}
                className={`pf-share-links__row pf-share-links__row--${l.state}`}
                data-testid="share-row"
                data-link={l.linkId}
                data-state={l.state}
              >
                <b className="pf-share-links__child">{l.child.nickname || "—"}</b>
                <span className="pf-chip-static">{t(`share.teacher.target.${l.target}`)}</span>
                <span className="pf-muted">
                  {t("share.teacher.dates", {
                    created: shareDate(l.createdAt) || "—",
                    expires: shareDate(l.expiresAt) || "—",
                  })}
                </span>
                <span className="pf-muted" data-testid="share-opens">
                  {t("share.teacher.opens", { n: l.openCount })}
                </span>
                <span
                  className={`pf-chip-static pf-share-links__state pf-share-links__state--${l.state}`}
                  data-testid="share-state"
                >
                  {t(`share.teacher.state.${l.state}`)}
                </span>
                <span className="pf-share-links__acts">
                  {active && (
                    <Button
                      variant="ghost"
                      icon="⧉"
                      className="pf-btn--sm"
                      disabled={!url}
                      title={url ? undefined : t("share.teacher.copyOnce")}
                      aria-label={t("share.teacher.copyOf", { child: l.child.nickname })}
                      data-testid="share-copy"
                      onClick={() => url && void copy(url)}
                    >
                      {t("share.teacher.copy")}
                    </Button>
                  )}
                  {active &&
                    (confirm === l.linkId ? (
                      <>
                        <Button
                          variant="ghost"
                          icon="🛑"
                          className="pf-btn--sm pf-share-links__danger"
                          disabled={busy !== null}
                          data-testid="share-revoke-yes"
                          onClick={() => void revoke(l)}
                        >
                          {t("share.teacher.revokeYes")}
                        </Button>
                        <Button
                          variant="ghost"
                          className="pf-btn--sm"
                          onClick={() => setConfirm(null)}
                        >
                          {t("share.teacher.revokeNo")}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="ghost"
                        icon="🛑"
                        className="pf-btn--sm"
                        disabled={busy !== null}
                        aria-label={t("share.teacher.revokeOf", { child: l.child.nickname })}
                        data-testid="share-revoke"
                        onClick={() => setConfirm(l.linkId)}
                      >
                        {t("share.teacher.revoke")}
                      </Button>
                    ))}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {made && (
        <div className="pf-banner pf-banner--ok" role="status" data-testid="share-made">
          <div className="pf-banner__text">
            <b>{t("share.teacher.madeTitle")}</b> {t("share.teacher.madeNext")}
            <code className="pf-share-links__url" data-testid="share-made-url">
              {made.url}
            </code>
          </div>
          <div className="pf-banner__actions">
            <Button
              variant="ghost"
              icon="⧉"
              className="pf-btn--sm"
              data-testid="share-made-copy"
              onClick={() => void copy(made.url)}
            >
              {t("share.teacher.copy")}
            </Button>
          </div>
        </div>
      )}
      {!nothing && (
        <div className="pf-share-links__make" data-testid="share-make">
          <label className="pf-field-row">
            <span className="pf-field-label">{t("share.teacher.child")}</span>
            <select
              className="pf-input"
              value={child}
              onChange={(e) => setChild(e.target.value)}
              data-testid="share-child"
            >
              <option value="">{t("share.teacher.childPick")}</option>
              {students.map((s) => (
                <option key={s.pid} value={s.pid}>
                  {s.nickname}
                </option>
              ))}
            </select>
          </label>
          <SegGroup label={t("share.teacher.days")} variant="pill" testId="share-days">
            {dayOptions(maxDays).map((d) => (
              <label key={d} className="pf-seg__opt">
                <input
                  type="radio"
                  name={`${hid}-days`}
                  checked={days === d}
                  onChange={() => setDays(d)}
                />
                <span>{t("share.teacher.daysN", { n: d })}</span>
              </label>
            ))}
          </SegGroup>
          {targets.length > 1 && (
            <SegGroup label={t("share.teacher.targetLabel")} variant="pill" testId="share-target">
              {targets.map((x) => (
                <label key={x} className="pf-seg__opt">
                  <input
                    type="radio"
                    name={`${hid}-target`}
                    checked={chosen === x}
                    onChange={() => setTarget(x)}
                  />
                  <span>{t(`share.teacher.target.${x}`)}</span>
                </label>
              ))}
            </SegGroup>
          )}
          <Button
            variant="green"
            icon="🔗"
            className="pf-btn--sm"
            disabled={!child || !chosen || busy !== null}
            data-testid="share-create"
            onClick={() => void create()}
          >
            {t("share.teacher.create", {
              days,
              target: chosen ? t(`share.teacher.target.${chosen}`) : "",
            })}
          </Button>
        </div>
      )}
    </section>
  );
}
